import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Tag,
  Layers,
  Folder,
  Package,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  RefreshCw,
  DollarSign,
  CheckCircle,
  BarChart3,
  FileText,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Clock,
  MoreHorizontal
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import MasterDataService from '../../services/masterDataService';
import { assetService } from '../../services/assetService';

const ActionDropdown = ({ category, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 200);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsOpen(false);
                  onView(category);
                }}
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Category
              </button>
            </li>

            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(category);
                }}
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Category
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(category);
                }}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Category
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const AssetCategorization = () => {
  // Add CSS animations for modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('categories');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toasts, removeToast, success, error: showError } = useToast();

  // Data state
  const [categorizationData, setCategorizationData] = useState([]);
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    totalAssets: 0,
    totalValue: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    categoryName: '',
    categoryCode: '',
    parentCategory: '',
    categoryType: 'Equipment',
    description: '',
    subcategories: '',
    depreciationRate: 20,
    usefulLife: 5,
    status: 'Active',
    tags: '',
    notes: ''
  });

  // Load categories from backend
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setIsLoading(true);
    try {
      // Load AssetType and AssetSubtype from master data
      const [assetTypesResponse, assetSubtypesResponse] = await Promise.all([
        MasterDataService.getByCategory('AssetType'),
        MasterDataService.getByCategory('AssetSubtype')
      ]);

      // API returns { success: true, data: [...] }
      // axios already extracts response.data, so we get { success: true, data: [...] }
      const assetTypes = assetTypesResponse?.data || assetTypesResponse || [];
      const assetSubtypes = assetSubtypesResponse?.data || assetSubtypesResponse || [];

      // Transform master data to category format
      const categories = Array.isArray(assetTypes) ? assetTypes : [];
      const transformedCategories = categories.map((type, index) => {
        const relatedSubtypes = Array.isArray(assetSubtypes) ? assetSubtypes.filter(
          st => st.parentValue === type.label || st.parentValue === type.code
        ) : [];

        // Preserve the MongoDB _id - this is critical for updates/deletes
        const mongoId = type._id || type.id || null;

        return {
          id: mongoId, // Use MongoDB _id directly
          _id: mongoId, // Also store as _id for consistency
          categoryName: type.label,
          categoryCode: type.code || type.label.substring(0, 5).toUpperCase(),
          parentCategory: type.parentValue || 'Root',
          categoryType: type.metadata?.categoryType || 'Equipment',
          description: type.description || '',
          subcategories: type.metadata?.subcategories ? type.metadata.subcategories.split(',').map(s => s.trim()).filter(Boolean) : relatedSubtypes.map(st => st.label),
          assetCount: 0, // Will be calculated from actual assets
          totalValue: 0, // Will be calculated from actual assets
          depreciationRate: type.metadata?.depreciationRate || 20,
          usefulLife: type.metadata?.usefulLife || 5,
          status: type.isActive !== false ? 'Active' : 'Inactive',
          createdDate: type.createdAt || new Date().toISOString().split('T')[0],
          lastModified: type.updatedAt || new Date().toISOString(),
          modifiedBy: 'System',
          tags: type.metadata?.tags ? type.metadata.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          notes: type.notes || ''
        };
      });

      // Load actual asset counts and values from assets API
      let totalAssets = 0;
      let totalValue = 0;

      try {
        const assetsResponse = await assetService.getAssets({ limit: 100 });
        const assets = assetsResponse?.data?.data?.assets || assetsResponse?.data?.assets || [];

        // Calculate asset counts and values by category
        const categoryStats = {};
        assets.forEach(asset => {
          const categoryName = asset.type || 'Other';
          if (!categoryStats[categoryName]) {
            categoryStats[categoryName] = { count: 0, value: 0 };
          }
          categoryStats[categoryName].count++;
          categoryStats[categoryName].value += asset.currentValue || asset.cost || 0;
        });

        // Update transformed categories with actual counts
        transformedCategories.forEach(cat => {
          const stats = categoryStats[cat.categoryName] || { count: 0, value: 0 };
          cat.assetCount = stats.count;
          cat.totalValue = stats.value;
          totalAssets += stats.count;
          totalValue += stats.value;
        });
      } catch (assetError) {
        console.warn('Error loading asset stats:', assetError);
        // Continue without asset stats
      }

      setCategorizationData(transformedCategories);

      // Calculate stats
      const totalCategories = transformedCategories.length;
      const activeCategories = transformedCategories.filter(c => c.status === 'Active').length;

      setStats({
        totalCategories,
        activeCategories,
        totalAssets,
        totalValue
      });
    } catch (error) {
      console.error('Error loading categories:', error);
      console.error('Error details:', error.response?.data || error.message);
      showError(error.response?.data?.message || error.message || 'Failed to load asset categories');
      // Fallback to empty array
      setCategorizationData([]);
      setStats({
        totalCategories: 0,
        activeCategories: 0,
        totalAssets: 0,
        totalValue: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered data
  const filteredCategories = categorizationData.filter(category => {
    const matchesSearch = searchTerm === '' ||
      category.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.categoryCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  // Build hierarchy tree from categories
  const buildHierarchy = (categories) => {
    if (!categories || categories.length === 0) {
      return [];
    }

    const categoryMap = new Map();
    const rootCategories = [];

    // First pass: create map of all categories
    categories.forEach(category => {
      categoryMap.set(category.categoryName, {
        ...category,
        children: [],
        isProcessed: false
      });
    });

    // Second pass: build tree structure
    categories.forEach(category => {
      const node = categoryMap.get(category.categoryName);
      if (!node) return;

      if (category.parentCategory && category.parentCategory !== 'Root' && categoryMap.has(category.parentCategory)) {
        const parent = categoryMap.get(category.parentCategory);
        if (parent !== node) { // Prevent self-reference
          parent.children.push(node);
          node.isProcessed = true;
        } else {
          // Self-reference detected, treat as root
          rootCategories.push(node);
        }
      } else {
        // No parent or parent is 'Root', treat as root category
        if (!node.isProcessed) {
          rootCategories.push(node);
          node.isProcessed = true;
        }
      }
    });

    // Sort root categories alphabetically
    rootCategories.sort((a, b) => a.categoryName.localeCompare(b.categoryName));

    // Sort children recursively
    const sortChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          node.children.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
          sortChildren(node.children);
        }
      });
    };
    sortChildren(rootCategories);

    return rootCategories;
  };

  // Render hierarchy tree node
  const renderHierarchyNode = (node, level = 0) => {
    const indent = level * 24;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node._id || node.id || node.categoryName} style={{ marginLeft: `${indent}px`, marginBottom: '8px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px',
          backgroundColor: level === 0 ? '#f8f9fa' : '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'background-color 0.2s'
        }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = level === 0 ? '#f8f9fa' : '#ffffff'}
          onClick={() => handleView(node)}
        >
          {hasChildren && (
            <Folder size={16} style={{ marginRight: '8px', color: '#4678be' }} />
          )}
          {!hasChildren && (
            <Package size={16} style={{ marginRight: '8px', color: '#9ca3af' }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
              {node.categoryName}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {node.categoryCode} {node.assetCount > 0 && `• ${node.assetCount} assets`} {node.totalValue > 0 && `• $${node.totalValue.toLocaleString()}`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              backgroundColor: node.status === 'Active' ? '#d1fae5' : '#fee2e2',
              color: node.status === 'Active' ? '#065f46' : '#991b1b'
            }}>
              {node.status}
            </span>
            {hasChildren && (
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '500',
                backgroundColor: '#dbeafe',
                color: '#1e40af'
              }}>
                {node.children.length} {node.children.length === 1 ? 'child' : 'children'}
              </span>
            )}
          </div>
        </div>
        {hasChildren && (
          <div style={{ marginTop: '4px' }}>
            {node.children.map(child => renderHierarchyNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const hierarchyTree = buildHierarchy(categorizationData);

  const tabs = [
    { id: 'categories', label: 'Categories', icon: <Target size={18} /> },
    { id: 'hierarchy', label: 'Category Hierarchy', icon: <Layers size={18} /> },
    { id: 'templates', label: 'Category Templates', icon: <FileText size={18} /> },
    { id: 'analytics', label: 'Category Analytics', icon: <BarChart3 size={18} /> }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'categoryName',
      label: 'Category',
      width: '200px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.categoryCode}</div>
        </div>
      )
    },
    {
      key: 'parentCategory',
      label: 'Parent Category',
      width: '150px',
      render: (parent) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: '#dbeafe',
          color: '#1e40af'
        }}>
          {parent}
        </span>
      )
    },
    {
      key: 'categoryType',
      label: 'Type',
      width: '120px',
      render: (type) => {
        const typeColors = {
          'Equipment': { bg: '#dbeafe', color: '#1e40af' },
          'Furniture': { bg: '#fef3c7', color: '#92400e' },
          'Vehicle': { bg: '#d1fae5', color: '#065f46' },
          'Property': { bg: '#e0e7ff', color: '#3730a3' },
          'License': { bg: '#f3e8ff', color: '#6b21a8' }
        };
        const colors = typeColors[type] || { bg: '#f3f4f6', color: '#374151' };
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: colors.bg,
            color: colors.color
          }}>
            {type}
          </span>
        );
      }
    },
    {
      key: 'assetCount',
      label: 'Asset Count',
      width: '120px',
      render: (count) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Package size={14} style={{ color: '#4678be' }} />
          <span style={{ fontWeight: '500' }}>{count}</span>
        </div>
      )
    },
    {
      key: 'totalValue',
      label: 'Total Value',
      width: '130px',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={14} style={{ color: '#4678be' }} />
          <span style={{ fontWeight: '500' }}>${value.toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'depreciationRate',
      label: 'Depreciation Rate',
      width: '140px',
      render: (rate) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontWeight: '500' }}>{rate}%</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (status) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: status === 'Active' ? '#d1fae5' : '#fee2e2',
          color: status === 'Active' ? '#065f46' : '#991b1b'
        }}>
          {status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '60px',
      render: (_, row) => (
        <ActionDropdown
          onView={() => handleView(row)}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDelete(row)}
        />
      )
    }
  ];

  // Handler functions
  const handleView = (category) => {
    setSelectedCategory(category);
    setShowViewModal(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      categoryName: category.categoryName,
      categoryCode: category.categoryCode,
      parentCategory: category.parentCategory,
      categoryType: category.categoryType,
      description: category.description,
      subcategories: category.subcategories.join(', '),
      depreciationRate: category.depreciationRate,
      usefulLife: category.usefulLife,
      status: category.status,
      tags: category.tags.join(', '),
      notes: category.notes || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    navigate('/assets/category/create');
  };

  // Header actions - defined after handlers
  const headerActions = [
    {
      label: '', // Icon-only button
      icon: <HelpCircle size={18} />,
      onClick: () => {
        window.open('/help/assets/asset-categorization.html', '_blank');
      },
      variant: 'outline',
      tooltip: 'Asset Categorization Help'
    },
    {
      label: '', // Icon-only button
      icon: <RefreshCw size={18} />,
      onClick: loadCategories,
      variant: 'outline',
      tooltip: 'Refresh categories'
    },
    {
      label: 'New Category',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
      tooltip: 'Create a new category'
    }
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validate required fields
      if (!formData.categoryName || formData.categoryName.trim() === '') {
        showError('Category name is required');
        setIsLoading(false);
        return;
      }

      const categoryData = {
        category: 'AssetType',
        code: formData.categoryCode || formData.categoryName.substring(0, 5).toUpperCase().replace(/\s+/g, '_'),
        label: formData.categoryName.trim(),
        description: formData.description?.trim() || '',
        parentValue: formData.parentCategory && formData.parentCategory !== 'Root' && formData.parentCategory !== '' ? formData.parentCategory : null,
        isActive: formData.status === 'Active',
        sortOrder: categorizationData.length + 1,
        notes: formData.notes?.trim() || '',
        metadata: {
          categoryType: formData.categoryType,
          depreciationRate: formData.depreciationRate,
          usefulLife: formData.usefulLife,
          subcategories: formData.subcategories,
          tags: formData.tags
        }
      };

      if (showEditModal && selectedCategory) {
        // Use _id if available, otherwise fall back to id
        const categoryId = selectedCategory._id || selectedCategory.id;
        if (!categoryId) {
          throw new Error('Category ID is missing. Cannot update.');
        }

        const response = await MasterDataService.update(categoryId, categoryData);
        if (response && response.success) {
          success('Category updated successfully!');
          await loadCategories();
          setShowEditModal(false);
          setSelectedCategory(null);
        } else {
          const errorMsg = response?.message || response?.error?.message || 'Failed to update category';
          throw new Error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      let errorMessage = 'Failed to save category. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      // Use _id if available, otherwise fall back to id
      const categoryId = selectedCategory._id || selectedCategory.id;
      if (!categoryId) {
        throw new Error('Category ID is missing. Cannot delete.');
      }

      const response = await MasterDataService.delete(categoryId);
      if (response && response.success) {
        success('Category deleted successfully!');
        await loadCategories();
        setShowDeleteModal(false);
        setSelectedCategory(null);
      } else {
        const errorMsg = response?.message || response?.error?.message || 'Failed to delete category';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      // Handle different error formats
      let errorMessage = 'Failed to delete category. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getTrendUI = (value) => {
    const isPositive = value >= 0;
    return (
      <div
        className="flex items-center gap-1 text-xs font-semibold"
        style={{ color: isPositive ? '#059669' : '#DC2626' }}
      >
        {isPositive ? (
          <ArrowUpRight size={14} />
        ) : (
          <ArrowDownRight size={14} />
        )}
        {Math.abs(value)}%
      </div>
    );
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Asset Categorization Management"
        description="Organize and manage asset categories, hierarchies, and classification systems"
        actions={headerActions}
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Tabbed Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">
                  {tab.icon}
                </div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid max-w-[1200px] mx-auto w-full mt-6 grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">

          {/* Total Categories */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            {/* Trend */}
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
              <ArrowUpRight size={14} />
              +6.4%
            </div>
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Target size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalCategories}
                </div>
                <div className="text-[13px] text-[var(--text-secondary)] mt-1">
                  Total Categories
                </div>
              </div>
            </div>
          </div>
          {/* Active Categories */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
              <ArrowUpRight size={14} />
              +4.1%
            </div>

            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <CheckCircle size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.activeCategories}
                </div>
                <div className="text-[13px] text-[var(--text-secondary)] mt-1">
                  Active Categories
                </div>
              </div>
            </div>
          </div>
          {/* Total Assets */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#DC2626]">
              <ArrowDownRight size={14} />
              -2.3%
            </div>
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <Package size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalAssets.toLocaleString()}
                </div>
                <div className="text-[13px] text-[var(--text-secondary)] mt-1">
                  Total Assets
                </div>
              </div>
            </div>
          </div>

          {/* Total Value */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />

            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
              <ArrowUpRight size={14} />
              +8.9%
            </div>
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  ${stats.totalValue.toLocaleString()}
                </div>
                <div className="text-[13px] text-[var(--text-secondary)] mt-1">
                  Total Value
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'categories' && (
          <div className="page-tab-content" style={{ marginTop: '24px' }}>
            <div className="page-table-container">
              <DataTable
                data={filteredCategories}
                columns={columns}
                title="Asset Categories"
                searchable={true}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
                showCheckboxes={false}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                searchPlaceholder="Search categories by name, code, or description..."
                emptyMessage="No categories found. Click 'New Category' to get started."
                initialHiddenColumns={['id']}
                loading={isLoading}
              />
            </div>
          </div>
        )}

        {activeTab === 'hierarchy' && (
          <div className="page-tab-content" style={{ marginTop: '24px' }}>
            <div className="card">
              <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '16px 20px' }}>
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Category Hierarchy</h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {isLoading ? (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <RefreshCw size={32} style={{ color: '#9ca3af', animation: 'spin 1s linear infinite' }} />
                    <p style={{ color: '#9ca3af', marginTop: '12px' }}>Loading hierarchy...</p>
                  </div>
                ) : hierarchyTree.length > 0 ? (
                  <div>
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', borderLeft: '4px solid #4678be', borderRadius: '4px' }}>
                      <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '500' }}>
                        <Layers size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Category Hierarchy Tree
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {categorizationData.length} total categories organized in hierarchical structure
                      </div>
                    </div>
                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      {hierarchyTree.map(node => renderHierarchyNode(node, 0))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <Layers size={48} style={{ color: '#9ca3af', marginBottom: '12px' }} />
                    <h4 style={{ color: '#6b7280', fontFamily: 'var(--font-family)', marginBottom: '8px' }}>No Categories Found</h4>
                    <p style={{ color: '#9ca3af', fontFamily: 'var(--font-family)' }}>Create categories to see the hierarchy structure</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="page-tab-content" style={{ marginTop: '24px' }}>
            <div className="card">
              <div className="card-body text-center" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                  <FileText size={48} style={{ color: '#9ca3af', marginBottom: '12px' }} />
                  <h4 style={{ color: '#6b7280', fontFamily: 'var(--font-family)' }}>Category Templates</h4>
                  <p style={{ color: '#9ca3af', fontFamily: 'var(--font-family)' }}>Coming Soon!</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="page-tab-content" style={{ marginTop: '24px' }}>
            <div className="card">
              <div className="card-body text-center" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                  <BarChart3 size={48} style={{ color: '#9ca3af', marginBottom: '12px' }} />
                  <h4 style={{ color: '#6b7280', fontFamily: 'var(--font-family)' }}>Category Analytics</h4>
                  <p style={{ color: '#9ca3af', fontFamily: 'var(--font-family)' }}>Coming Soon!</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Edit Category Modal - Full Viewport Side Panel */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-in-out'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '100%',
            maxWidth: '700px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInFromRight 0.3s ease-out',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              backgroundColor: '#4678be',
              color: 'white',
              flexShrink: 0
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
                margin: 0,
                fontFamily: 'var(--font-family)'
              }}>
                Edit Category
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCategory(null);
                  setFormData({
                    categoryName: '',
                    categoryCode: '',
                    parentCategory: '',
                    categoryType: 'Equipment',
                    description: '',
                    subcategories: '',
                    depreciationRate: 20,
                    usefulLife: 5,
                    status: 'Active',
                    tags: '',
                    notes: ''
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '4px 8px',
                  lineHeight: '1',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleFormSubmit} style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '24px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {/* Row 1 */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.categoryName}
                    onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Category Code *
                  </label>
                  <input
                    type="text"
                    value={formData.categoryCode}
                    onChange={(e) => setFormData({ ...formData, categoryCode: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                {/* Row 2 */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Parent Category
                  </label>
                  <input
                    type="text"
                    value={formData.parentCategory}
                    onChange={(e) => setFormData({ ...formData, parentCategory: e.target.value })}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Category Type *
                  </label>
                  <select
                    value={formData.categoryType}
                    onChange={(e) => setFormData({ ...formData, categoryType: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="Equipment">Equipment</option>
                    <option value="Furniture">Furniture</option>
                    <option value="Vehicle">Vehicle</option>
                    <option value="Property">Property</option>
                    <option value="License">License</option>
                  </select>
                </div>

                {/* Row 3 */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Depreciation Rate (%) *
                  </label>
                  <input
                    type="number"
                    value={formData.depreciationRate}
                    onChange={(e) => setFormData({ ...formData, depreciationRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.1"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Useful Life (Years) *
                  </label>
                  <input
                    type="number"
                    value={formData.usefulLife}
                    onChange={(e) => setFormData({ ...formData, usefulLife: parseInt(e.target.value) || 0 })}
                    min="1"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                {/* Row 4 */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>

                {/* Subcategories (spans full width) */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Subcategories (Comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.subcategories}
                    onChange={(e) => setFormData({ ...formData, subcategories: e.target.value })}
                    placeholder="e.g., Laptops, Desktops, Servers"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Enter comma-separated values to automatically create subcategories under this category.
                  </small>
                </div>

                {/* Last Row: Description (spans full width) */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Notes (spans full width) */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCategory(null);
                    setFormData({
                      categoryName: '',
                      categoryCode: '',
                      parentCategory: '',
                      categoryType: 'Equipment',
                      description: '',
                      subcategories: '',
                      depreciationRate: 20,
                      usefulLife: 5,
                      status: 'Active',
                      tags: '',
                      notes: ''
                    });
                  }}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: '#4678be',
                    border: 'none',
                    borderRadius: '0',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    'Update Category'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Category Modal */}
      {showViewModal && selectedCategory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  Category Details
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
                  {selectedCategory.categoryCode}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Top Summary Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Name</label>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>{selectedCategory.categoryName}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Status</label>
                  <div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: selectedCategory.status === 'Active' ? '#d1fae5' : '#fee2e2',
                      color: selectedCategory.status === 'Active' ? '#065f46' : '#991b1b'
                    }}>
                      {selectedCategory.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Grid Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Type</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{selectedCategory.categoryType}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Parent</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{selectedCategory.parentCategory}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Assets</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{selectedCategory.assetCount}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Value</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>${selectedCategory.totalValue?.toLocaleString()}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Depreciation Rate</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{selectedCategory.depreciationRate}%</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Useful Life</label>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{selectedCategory.usefulLife} Years</div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Description</label>
                <div style={{
                  fontSize: '14px',
                  color: '#4b5563',
                  lineHeight: '1.5',
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '4px'
                }}>
                  {selectedCategory.description || 'No description provided.'}
                </div>
              </div>

              {/* Subcategories (if any) */}
              {selectedCategory.subcategories && selectedCategory.subcategories.length > 0 && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Subcategories</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedCategory.subcategories.map((sub, idx) => (
                      <span key={idx} style={{
                        padding: '4px 10px',
                        backgroundColor: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {sub}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Asset Category"
        message={`Are you sure you want to delete category "${selectedCategory?.categoryName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AssetCategorization;
